import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Inbox, Send, CheckCircle, XCircle, Eye, Clock, ThumbsUp, AlertTriangle, FileText, Download, Calendar, User, Building, FileCheck, Users, Search, Trash2, Square, CheckSquare, History, ArrowRight, Upload, X, Plus, File as FileIcon } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const BranchCommunication = ({ branchIdOverride, readOnly = false }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [subTab, setSubTab] = useState('incoming'); // Alt tab: incoming, outgoing, approved, rejected, forwarded, dept_approved, dept_rejected, hr_approved
  const [inboxRequests, setInboxRequests] = useState([]);
  const [forwardedRequests, setForwardedRequests] = useState([]);
  const [rejectedRequests, setRejectedRequests] = useState([]);
  const [rejectedByHRRequests, setRejectedByHRRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [hrApprovedRequests, setHrApprovedRequests] = useState([]); // İK/Yönetim onaylanan
  const [loading, setLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  
  // Yeni state'ler: Kategoriye göre iletilen, departman onaylanan ve departman reddedilen
  const [forwardedByCategory, setForwardedByCategory] = useState([]);
  const [deptApprovedByCategory, setDeptApprovedByCategory] = useState([]);
  const [deptRejectedByCategory, setDeptRejectedByCategory] = useState([]);
  
  // Modal states
  const [showRequestDetailModal, setShowRequestDetailModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(''); // 'forward_hr' or 'reject'
  const [actionNote, setActionNote] = useState('');
  const [actionDocuments, setActionDocuments] = useState([]); // Ek belgeler
  
  // Kullanıcı seçim modalı state'leri (Genel Talepler için)
  const [showUserSelectModal, setShowUserSelectModal] = useState(false);
  const [usersForForward, setUsersForForward] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [forwardNote, setForwardNote] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [forwardToUserId, setForwardToUserId] = useState('');  // İK Panel talepleri için iletme
  const [forwardUsers, setForwardUsers] = useState([]);  // İletme için kullanıcı listesi
  
  // Çoklu seçim state'leri
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Kullanıcı ID'sini al
  useEffect(() => {
    const token = localStorage.getItem('fortis_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.sub);
      } catch (e) {
        console.error('Token parse hatası:', e);
      }
    }
  }, []);
  
  // Bir talebin okunup okunmadığını kontrol et
  const isUnread = (request) => {
    if (!currentUserId || !request) return false;
    const readBy = request.read_by || {};
    return readBy[currentUserId] !== true;
  };
  
  // Ana kategoriler
  const mainCategories = [
    { id: 'all', label: 'TÜMÜ' },
    { id: 'my_requests', label: 'TALEPLERİM' },
    { id: 'ik', label: 'İK' },
    { id: 'satin_alma', label: 'SATIN ALMA' },
    { id: 'muhasebe', label: 'MUHASEBE' },
    { id: 'idari_isler', label: 'İDARİ İŞLER' },
    { id: 'genel_talepler', label: 'GENEL TALEPLER' },
    { id: 'yonetim', label: 'YÖNETİM' }
  ];
  
  // TALEPLERİM state'leri
  const [myRequests, setMyRequests] = useState([]);
  const [myRequestsLoading, setMyRequestsLoading] = useState(false);
  const [myRequestsSubTab, setMyRequestsSubTab] = useState('pending');
  const [createRequestModalOpen, setCreateRequestModalOpen] = useState(false);
  const [newRequestData, setNewRequestData] = useState({
    category: 'ik',
    title: '',
    description: '',
    priority: 'normal',
    target_user_id: '',
    documents: [],
    expected_delivery_date: ''  // Teslim tarihi (satın alma için)
  });
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestTargetUsers, setRequestTargetUsers] = useState([]);
  const [loadingRequestUsers, setLoadingRequestUsers] = useState(false);
  const [requestUserSearchTerm, setRequestUserSearchTerm] = useState('');
  
  // Kategorilere göre talep başlıkları
  const requestTitles = {
    ik: ['İzin Talebi', 'Avans Talebi', 'Maaş Bilgi Talebi', 'Belge Talebi', 'Diğer İK Talebi'],
    satin_alma: ['Malzeme Talebi', 'Ekipman Talebi', 'Ofis Malzemesi Talebi', 'Diğer Satın Alma Talebi'],
    muhasebe: ['Fatura Bilgi Talebi', 'Ödeme Talebi', 'Masraf Talebi', 'Diğer Muhasebe Talebi'],
    idari_isler: ['Bakım/Onarım Talebi', 'Temizlik Talebi', 'Güvenlik Talebi', 'Diğer İdari Talep'],
    genel_talepler: ['Depo Ürün İsteği', 'Genel Bilgi Talebi', 'Diğer Talep']
  };
  
  const getCurrentTitles = () => requestTitles[newRequestData.category] || [];
  
  // Kategori etiketini getir
  const getCategoryLabel = (categoryId) => {
    const labels = {
      'ik': 'İK',
      'satin_alma': 'Satın Alma',
      'muhasebe': 'Muhasebe',
      'idari_isler': 'İdari İşler',
      'genel_talepler': 'Genel Talepler',
      'yonetim': 'Yönetim',
      'all': 'Tümü',
      'my_requests': 'Taleplerim'
    };
    return labels[categoryId] || categoryId;
  };
  
  // Alt tablar - kategoriye göre dinamik olacak
  const getSubTabs = () => {
    // TALEPLERİM sekmesi için özel tablar
    if (activeTab === 'my_requests') {
      return [
        { id: 'pending', label: 'Bekleyen', icon: Clock },
        { id: 'approved', label: 'Kabul Edilen', icon: CheckCircle },
        { id: 'rejected', label: 'Red Edilen', icon: XCircle }
      ];
    }
    
    const baseTabs = [
      { id: 'incoming', label: 'Gelen', icon: Inbox },
      { id: 'approved', label: 'Onayladıklarım', icon: CheckCircle },
      { id: 'rejected', label: 'Red Ettiklerim', icon: XCircle }
    ];
    
    // Kategoriye özel tablar ekle (all ve yonetim hariç)
    if (activeTab !== 'all' && activeTab !== 'yonetim') {
      const categoryLabels = {
        'ik': 'İK',
        'satin_alma': 'Satın Alma',
        'muhasebe': 'Muhasebe',
        'idari_isler': 'İdari İşler',
        'genel_talepler': 'İletilen'
      };
      
      const categoryLabel = categoryLabels[activeTab] || activeTab;
      
      // İletilen tabı
      baseTabs.push({
        id: 'forwarded_dept',
        label: activeTab === 'genel_talepler' ? 'İletilen' : `${categoryLabel}'ya İletilen`,
        icon: Send
      });
      
      // Departman onaylanan tabı
      baseTabs.push({
        id: 'dept_approved',
        label: activeTab === 'genel_talepler' ? 'Onaylanan' : `${categoryLabel} Onaylanan`,
        icon: CheckCircle
      });
      
      // Departman red edilen tabı
      baseTabs.push({
        id: 'dept_rejected',
        label: activeTab === 'genel_talepler' ? 'Red Edilen' : `${categoryLabel} Reddedilen`,
        icon: XCircle
      });
    }
    
    return baseTabs;
  };
  
  const subTabs = getSubTabs();
  
  // Kategoriye göre talep filtrele
  const filterByCategory = (requests, category) => {
    if (category === 'all') return requests;
    return requests.filter(req => {
      const formData = req.form_data?.form_data || req.form_data || {};
      const reqCategory = formData.category || req.category || 'ik';
      return reqCategory === category;
    });
  };
  
  // Alt tab ve kategoriye göre talepleri getir
  const getFilteredRequests = () => {
    let requests = [];
    switch (subTab) {
      case 'incoming':
        requests = inboxRequests;
        break;
      case 'approved':
        requests = approvedRequests;
        break;
      case 'rejected':
        requests = [...rejectedRequests, ...rejectedByHRRequests];
        break;
      case 'forwarded_dept':
        requests = forwardedByCategory;
        return requests; // Zaten kategoriye göre filtrelenmiş
      case 'dept_approved':
        requests = deptApprovedByCategory;
        return requests; // Zaten kategoriye göre filtrelenmiş
      case 'dept_rejected':
        requests = deptRejectedByCategory;
        return requests; // Zaten kategoriye göre filtrelenmiş
      default:
        requests = inboxRequests;
    }
    return filterByCategory(requests, activeTab);
  };
  
  // Alt tab için okunmamış sayısını hesapla
  const getSubTabUnreadCount = (tabId) => {
    let requests = [];
    switch (tabId) {
      case 'incoming':
        requests = inboxRequests;
        break;
      case 'approved':
        requests = approvedRequests;
        break;
      case 'rejected':
        requests = [...rejectedRequests, ...rejectedByHRRequests];
        break;
      case 'forwarded_dept':
        return forwardedByCategory.filter(req => isUnread(req)).length;
      case 'dept_approved':
        return deptApprovedByCategory.filter(req => isUnread(req)).length;
      case 'dept_rejected':
        return deptRejectedByCategory.filter(req => isUnread(req)).length;
      default:
        requests = [];
    }
    return filterByCategory(requests, activeTab).filter(req => isUnread(req)).length;
  };
  
  // Ana kategori için okunmamış sayısını hesapla
  const getCategoryUnreadCount = (categoryId) => {
    const allRequests = [...inboxRequests, ...forwardedRequests, ...approvedRequests, ...rejectedRequests, ...rejectedByHRRequests, ...forwardedByCategory, ...deptApprovedByCategory, ...deptRejectedByCategory];
    return filterByCategory(allRequests, categoryId).filter(req => isUnread(req)).length;
  };
  
  // Toplam okunmamış sayısı
  const getTotalUnreadCount = () => {
    const allRequests = [...inboxRequests, ...forwardedRequests, ...approvedRequests, ...rejectedRequests, ...rejectedByHRRequests, ...forwardedByCategory, ...deptApprovedByCategory, ...deptRejectedByCategory];
    return allRequests.filter(req => isUnread(req)).length;
  };
  
  // Alt tab için sayı hesapla
  const getSubTabCount = (tabId) => {
    let requests = [];
    switch (tabId) {
      case 'incoming':
        requests = inboxRequests;
        break;
      case 'approved':
        requests = approvedRequests;
        break;
      case 'rejected':
        requests = [...rejectedRequests, ...rejectedByHRRequests];
        break;
      case 'forwarded_dept':
        return forwardedByCategory.length;
      case 'dept_approved':
        return deptApprovedByCategory.length;
      case 'dept_rejected':
        return deptRejectedByCategory.length;
      default:
        requests = [];
    }
    return filterByCategory(requests, activeTab).length;
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Okundu olarak işaretle
  const markAsRead = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      await fetch(`${API_URL}/api/notifications/mark-read?item_type=hr_request&mark_all=true&category=branch_inbox`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Okundu işaretleme hatası:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchAllData();
    // Sayfa açıldığında okundu olarak işaretle
    markAsRead();
  }, [markAsRead]);

  const fetchAllData = () => {
    fetchInboxRequests();
    fetchForwardedRequests();
    fetchRejectedRequests();
    fetchRejectedByHRRequests();
    fetchApprovedRequests();
    fetchMyRequests();
  };

  // TALEPLERİM - Kendi oluşturduğum talepleri getir
  const fetchMyRequests = async () => {
    setMyRequestsLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/work/requests/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyRequests(data);
      }
    } catch (error) {
      console.error('Taleplerim yüklenemedi:', error);
    }
    setMyRequestsLoading(false);
  };

  // Talep oluşturma için kullanıcı listesini yükle
  const fetchRequestTargetUsers = async () => {
    setLoadingRequestUsers(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/branch/users-for-forward`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRequestTargetUsers(data.users || []);
      }
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
    } finally {
      setLoadingRequestUsers(false);
    }
  };

  // Filtrelenmiş talep hedef kullanıcıları
  const filteredRequestTargetUsers = requestTargetUsers.filter(user => 
    user.name?.toLowerCase().includes(requestUserSearchTerm.toLowerCase()) ||
    user.position?.toLowerCase().includes(requestUserSearchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(requestUserSearchTerm.toLowerCase())
  );

  // Modal açılınca kullanıcıları yükle
  const openCreateRequestModal = () => {
    setNewRequestData({
      category: 'ik',
      title: '',
      description: '',
      priority: 'normal',
      target_user_id: '',
      documents: [],
      expected_delivery_date: ''
    });
    setPendingDocuments([]);
    setRequestUserSearchTerm('');
    fetchRequestTargetUsers();
    setCreateRequestModalOpen(true);
  };

  // Yeni talep oluştur
  const handleCreateRequest = async () => {
    // Muhasebe için belge zorunlu, diğerleri için başlık zorunlu
    if (newRequestData.category === 'muhasebe') {
      if (pendingDocuments.length === 0) {
        toast.error('Muhasebe talebi için en az bir belge yüklemeniz gerekiyor');
        return;
      }
      // Muhasebe için başlık dosya adından otomatik gelir
      if (!newRequestData.title.trim()) {
        toast.error('Lütfen bir belge yükleyin');
        return;
      }
    } else {
      if (!newRequestData.title.trim()) {
        toast.error('Talep başlığı gerekli');
        return;
      }
      if (!newRequestData.description.trim()) {
        toast.error('Talep açıklaması gerekli');
        return;
      }
    }

    setSubmittingRequest(true);
    try {
      const token = localStorage.getItem('fortis_token');
      
      // FormData ile gönder (belgeler için)
      const formData = new FormData();
      formData.append('category', newRequestData.category);
      formData.append('title', newRequestData.title);
      formData.append('description', newRequestData.description || '');
      formData.append('priority', newRequestData.category === 'muhasebe' ? 'normal' : newRequestData.priority);
      
      // Hedef kullanıcı varsa ekle (muhasebe hariç)
      if (newRequestData.target_user_id && newRequestData.category !== 'muhasebe') {
        formData.append('target_user_id', newRequestData.target_user_id);
      }
      
      // Teslim tarihi varsa ekle (satın alma için)
      if (newRequestData.expected_delivery_date) {
        formData.append('expected_delivery_date', newRequestData.expected_delivery_date);
      }
      
      // Belgeleri ekle
      pendingDocuments.forEach((file) => {
        formData.append('documents', file);
      });

      const response = await fetch(`${API_URL}/api/work/requests`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        toast.success('Talep başarıyla oluşturuldu');
        setCreateRequestModalOpen(false);
        setNewRequestData({
          category: 'ik',
          title: '',
          description: '',
          priority: 'normal',
          target_user_id: '',
          documents: [],
          expected_delivery_date: ''
        });
        setPendingDocuments([]);
        fetchMyRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Talep oluşturulamadı');
      }
    } catch (error) {
      console.error('Talep oluşturma hatası:', error);
      toast.error('Talep oluşturulamadı');
    }
    setSubmittingRequest(false);
  };

  // Belge seçimi
  const handleDocumentSelect = (e) => {
    const files = Array.from(e.target.files);
    setPendingDocuments(prev => [...prev, ...files]);
    
    // İlk dosya yüklendiğinde dosya adını iş konusu olarak kullan
    if (files.length > 0 && !newRequestData.title) {
      const fileName = files[0].name;
      const titleFromFile = fileName.replace(/\.[^/.]+$/, "");
      setNewRequestData(prev => ({...prev, title: titleFromFile}));
    }
  };

  // Bekleyen belgeyi kaldır
  const removeDocument = (index) => {
    setPendingDocuments(prev => prev.filter((_, i) => i !== index));
  };

  // TALEPLERİM filtreleme
  const getMyFilteredRequests = () => {
    if (!myRequests) return [];
    
    switch (myRequestsSubTab) {
      case 'pending':
        return myRequests.filter(req => 
          req.status === 'pending' || 
          req.status === 'pending_muhasebe' ||
          req.status === 'forwarded_to_hr' || 
          req.status === 'forwarded_to_dept' ||
          req.status === 'forwarded_to_user'
        );
      case 'approved':
        return myRequests.filter(req => 
          req.status === 'approved' || 
          req.status === 'approved_by_hr' || 
          req.status === 'approved_by_dept' ||
          req.status === 'answered'
        );
      case 'rejected':
        return myRequests.filter(req => 
          req.status === 'rejected' || 
          req.status === 'rejected_by_hr' || 
          req.status === 'rejected_by_dept' || 
          req.status === 'rejected_by_branch'
        );
      default:
        return myRequests;
    }
  };

  const fetchInboxRequests = async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/branch/hr-requests/inbox`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInboxRequests(data);
      }
    } catch (error) {
      console.error('Gelen talepler yüklenemedi:', error);
    }
  };

  const fetchForwardedRequests = async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/branch/hr-requests/forwarded`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForwardedRequests(data);
      }
    } catch (error) {
      console.error('İK\'ya iletilen talepler yüklenemedi:', error);
    }
  };

  const fetchRejectedRequests = async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/branch/hr-requests/rejected`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRejectedRequests(data);
      }
    } catch (error) {
      console.error('Red edilen talepler yüklenemedi:', error);
    }
  };

  const fetchRejectedByHRRequests = async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/branch/hr-requests/rejected-by-hr`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRejectedByHRRequests(data);
      }
    } catch (error) {
      console.error('İK tarafından red edilen talepler yüklenemedi:', error);
    }
  };

  const fetchApprovedRequests = async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Şubenin DİREKT onayladığı talepler (İK'ya iletilmeden direkt onaylananlar)
      const branchApprovedResponse = await fetch(`${API_URL}/api/branch/hr-requests/approved`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let allApproved = [];
      
      if (branchApprovedResponse.ok) {
        const branchData = await branchApprovedResponse.json();
        allApproved = [...allApproved, ...branchData];
      }
      
      // Tarihe göre sırala (en yeni en üstte)
      allApproved.sort((a, b) => {
        const dateA = new Date(a.branch_approved_at || a.updated_at || a.created_at);
        const dateB = new Date(b.branch_approved_at || b.updated_at || b.created_at);
        return dateB - dateA;
      });
      
      setApprovedRequests(allApproved);
    } catch (error) {
      console.error('Onaylanan talepler yüklenemedi:', error);
    }
  };

  // Kategoriye göre iletilen talepleri getir
  const fetchForwardedByCategory = async (category) => {
    if (category === 'all' || category === 'yonetim') {
      setForwardedByCategory([]);
      return;
    }
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/branch/requests/forwarded-by-category/${category}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForwardedByCategory(data);
      } else {
        setForwardedByCategory([]);
      }
    } catch (error) {
      console.error('İletilen talepler yüklenemedi:', error);
      setForwardedByCategory([]);
    }
  };

  // Departman onayladığı talepleri getir
  const fetchDeptApprovedByCategory = async (category) => {
    if (category === 'all' || category === 'yonetim') {
      setDeptApprovedByCategory([]);
      return;
    }
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/branch/requests/department-approved/${category}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDeptApprovedByCategory(data);
      } else {
        setDeptApprovedByCategory([]);
      }
    } catch (error) {
      console.error('Departman onayladığı talepler yüklenemedi:', error);
      setDeptApprovedByCategory([]);
    }
  };

  // Departman reddettiği talepleri getir
  const fetchDeptRejectedByCategory = async (category) => {
    if (category === 'all' || category === 'yonetim') {
      setDeptRejectedByCategory([]);
      return;
    }
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/branch/requests/department-rejected/${category}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDeptRejectedByCategory(data);
      } else {
        setDeptRejectedByCategory([]);
      }
    } catch (error) {
      console.error('Departman reddettiği talepler yüklenemedi:', error);
      setDeptRejectedByCategory([]);
    }
  };

  // Kategori değiştiğinde ilgili verileri getir
  useEffect(() => {
    if (activeTab !== 'all' && activeTab !== 'yonetim') {
      fetchForwardedByCategory(activeTab);
      fetchDeptApprovedByCategory(activeTab);
      fetchDeptRejectedByCategory(activeTab);
    } else {
      setForwardedByCategory([]);
      setDeptApprovedByCategory([]);
      setDeptRejectedByCategory([]);
    }
  }, [activeTab]);

  const handleViewRequest = async (request) => {
    setSelectedRequest(request);
    setShowRequestDetailModal(true);
    
    // Talebi okundu olarak işaretle
    if (isUnread(request)) {
      try {
        const token = localStorage.getItem('fortis_token');
        const response = await fetch(`${API_URL}/api/notifications/mark-read?item_type=hr_request&item_id=${request.id}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          // Local state'i güncelle - read_by'a kullanıcıyı ekle
          const updateReadStatus = (requests, setRequests) => {
            setRequests(prev => prev.map(r => {
              if (r.id === request.id) {
                return { ...r, read_by: { ...r.read_by, [currentUserId]: true } };
              }
              return r;
            }));
          };
          
          updateReadStatus(inboxRequests, setInboxRequests);
          updateReadStatus(forwardedRequests, setForwardedRequests);
          updateReadStatus(approvedRequests, setApprovedRequests);
          updateReadStatus(rejectedRequests, setRejectedRequests);
          updateReadStatus(rejectedByHRRequests, setRejectedByHRRequests);
          updateReadStatus(forwardedByCategory, setForwardedByCategory);
          updateReadStatus(deptApprovedByCategory, setDeptApprovedByCategory);
          updateReadStatus(deptRejectedByCategory, setDeptRejectedByCategory);
          
          // Sidebar sayacını güncelle
          window.dispatchEvent(new CustomEvent('unread-counts-updated'));
        }
      } catch (error) {
        console.error('Okundu işaretleme hatası:', error);
      }
    }
  };

  const openActionModal = async (type) => {
    setActionType(type);
    setActionNote('');
    setActionDocuments([]); // Belgeleri sıfırla
    setForwardToUserId(''); // İletme kullanıcısını sıfırla
    
    // İletme için kullanıcı listesini yükle
    if (type === 'forward_hr_panel') {
      try {
        const token = localStorage.getItem('fortis_token');
        const response = await fetch(`${API_URL}/api/hr/users-for-request`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setForwardUsers(data.users || []);
        }
      } catch (error) {
        console.error('Kullanıcılar yüklenemedi:', error);
      }
    }
    
    setShowActionModal(true);
  };

  // Çoklu seçim fonksiyonları
  const toggleRequestSelection = (requestId, e) => {
    e.stopPropagation();
    setSelectedRequests(prev => 
      prev.includes(requestId) 
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  const selectAllRequests = (requests) => {
    const requestIds = requests.map(r => r.id);
    const allSelected = requestIds.every(id => selectedRequests.includes(id));
    
    if (allSelected) {
      setSelectedRequests(prev => prev.filter(id => !requestIds.includes(id)));
    } else {
      setSelectedRequests(prev => [...new Set([...prev, ...requestIds])]);
    }
  };

  const clearSelection = () => {
    setSelectedRequests([]);
  };

  // Seçili talepleri sil
  const handleDeleteSelected = async () => {
    if (selectedRequests.length === 0) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('fortis_token');
      let successCount = 0;
      let failCount = 0;
      
      for (const requestId of selectedRequests) {
        try {
          const response = await fetch(`${API_URL}/api/branch/requests/${requestId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} talep silindi`);
        fetchAllData();
      }
      if (failCount > 0) {
        toast.error(`${failCount} talep silinemedi`);
      }
      
      setSelectedRequests([]);
      setShowDeleteConfirmModal(false);
    } catch (error) {
      toast.error('Silme işlemi sırasında hata oluştu');
    } finally {
      setDeleting(false);
    }
  };

  // Kullanıcı listesini getir (Genel Talepler için)
  const fetchUsersForForward = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/branch/users-for-forward`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsersForForward(data.users || []);
      }
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
      toast.error('Kullanıcı listesi alınamadı');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Kullanıcı seçim modalını aç
  const openUserSelectModal = () => {
    setSelectedUserId(null);
    setUserSearchTerm('');
    setForwardNote('');
    fetchUsersForForward();
    setShowUserSelectModal(true);
  };

  // Seçilen kullanıcıya ilet
  const handleForwardToUser = async () => {
    if (!selectedUserId) {
      toast.error('Lütfen bir kullanıcı seçin');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(
        `${API_URL}/api/branch/work-requests/${selectedRequest.id}/forward-to-user?target_user_id=${selectedUserId}&note=${encodeURIComponent(forwardNote || '')}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Talep iletildi');
        setShowUserSelectModal(false);
        setShowRequestDetailModal(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Filtrelenmiş kullanıcılar
  const filteredUsers = usersForForward.filter(user => 
    user.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.position?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const handleAction = async () => {
    if ((actionType === 'reject' || actionType === 'reject_work' || actionType === 'reject_hr_panel') && !actionNote.trim()) {
      toast.error('Red nedeni gerekli');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Belgeleri base64'e çevir
      const documentsBase64 = [];
      for (const file of actionDocuments) {
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
        documentsBase64.push({
          name: file.name,
          type: file.type,
          data: base64
        });
      }
      
      // İK Panel talepleri için özel endpoint
      if (selectedRequest?.source === 'hr_panel') {
        let hrPanelAction = 'approve';
        if (actionType === 'reject_hr_panel') hrPanelAction = 'reject';
        else if (actionType === 'forward_hr_panel') hrPanelAction = 'forward';
        else if (actionType === 'approve_hr_panel') hrPanelAction = 'approve';
        
        const formData = new FormData();
        formData.append('action', hrPanelAction);
        if (actionNote) formData.append('notes', actionNote);
        if (hrPanelAction === 'forward' && forwardToUserId) {
          formData.append('forward_to_user_ids', JSON.stringify([forwardToUserId]));
        }
        
        const response = await fetch(
          `${API_URL}/api/hr/requests/${selectedRequest.id}/action`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || 'İşlem başarılı');
          setShowActionModal(false);
          setShowRequestDetailModal(false);
          setActionDocuments([]);
          setForwardToUserId('');
          fetchAllData();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'İşlem başarısız');
        }
      }
      // İş Paneli talepleri için ayrı endpoint kullan
      else if (actionType === 'approve_work' || actionType === 'reject_work' || actionType === 'approve_only') {
        const workAction = actionType === 'approve_work' ? 'approve' : 
                          actionType === 'approve_only' ? 'approve-only' : 'reject';
        const response = await fetch(
          `${API_URL}/api/branch/work-requests/${selectedRequest.id}/${workAction}`,
          {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              note: actionNote || '',
              documents: documentsBase64
            })
          }
        );
        
        if (response.ok) {
          if (actionType === 'approve_only') {
            toast.success('Talep onaylandı');
          } else if (actionType === 'approve_work') {
            const formData = selectedRequest.form_data?.form_data || selectedRequest.form_data || {};
            const category = formData.category || selectedRequest.category;
            if (formData.title === 'Depo Ürün İsteği') {
              toast.success('Talep onaylandı ve Depo Paneline iletildi');
            } else if (category === 'ik') {
              toast.success('Talep onaylandı ve İK Birimine iletildi');
            } else {
              toast.success('Talep onaylandı ve iletildi');
            }
          } else {
            toast.success('Talep reddedildi');
          }
          setShowActionModal(false);
          setShowRequestDetailModal(false);
          setActionDocuments([]);
          fetchAllData();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'İşlem başarısız');
        }
      } else {
        // HR talepleri için mevcut endpoint
        const response = await fetch(
          `${API_URL}/api/branch/hr-requests/${selectedRequest.id}/action`,
          {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              action: actionType,
              note: actionNote || '',
              documents: documentsBase64
            })
          }
        );
        
        if (response.ok) {
          toast.success(actionType === 'forward_hr' ? 'Talep İK birimine iletildi' : 'Talep reddedildi');
          setShowActionModal(false);
          setShowRequestDetailModal(false);
          setActionDocuments([]);
          fetchAllData();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'İşlem başarısız');
        }
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: 'Bekliyor', className: 'bg-amber-100 text-amber-800 border-amber-200' },
      'pending_branch': { label: 'Onay Bekliyor', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
      'pending_muhasebe': { label: 'Muhasebe\'de Bekliyor', className: 'bg-purple-100 text-purple-800 border-purple-200' },
      'pending_hr': { label: 'İK\'da Bekliyor', className: 'bg-blue-100 text-blue-800 border-blue-200' },
      'pending_management': { label: 'Yönetimde Bekliyor', className: 'bg-purple-100 text-purple-800 border-purple-200' },
      'approved': { label: 'Onaylandı', className: 'bg-green-100 text-green-800 border-green-200' },
      'approved_by_branch': { label: 'Şube Onayladı', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
      'rejected': { label: 'Reddedildi', className: 'bg-red-100 text-red-800 border-red-200' },
      'rejected_by_branch': { label: 'Red Ettiniz', className: 'bg-red-100 text-red-800 border-red-200' },
      'rejected_by_hr': { label: 'İK Reddetti', className: 'bg-red-100 text-red-800 border-red-200' },
      'rejected_by_management': { label: 'Yönetim Reddetti', className: 'bg-red-100 text-red-800 border-red-200' },
      'forwarded_to_hr': { label: 'İK\'ya İletildi', className: 'bg-blue-100 text-blue-800 border-blue-200' },
      'forwarded_to_user': { label: 'Kullanıcıya İletildi', className: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
      'forwarded_to_accounting': { label: 'Muhasebe\'ye İletildi', className: 'bg-purple-100 text-purple-800 border-purple-200' },
      'converted_to_job': { label: 'İşleniyor', className: 'bg-indigo-100 text-indigo-800 border-indigo-200' }
    };
    const statusInfo = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800 border-gray-200' };
    return <Badge variant="outline" className={statusInfo.className}>{statusInfo.label}</Badge>;
  };

  // Kategoriye göre onay buton metnini getir
  const getApproveButtonText = (request) => {
    const formData = request?.form_data?.form_data || request?.form_data || {};
    const category = formData.category || request?.category || '';
    
    const categoryLabels = {
      'ik': "Onayla ve İK'ya İlet",
      'satin_alma': "Onayla ve Satın Alma'ya İlet",
      'muhasebe': "Onayla ve Muhasebe'ye İlet",
      'idari_isler': "Onayla ve İdari İşler'e İlet",
      'yonetim': "Onayla ve Yönetim'e İlet",
      'genel_talepler': 'Onayla ve İlet'
    };
    
    return categoryLabels[category] || 'Onayla';
  };

  // Kategorinin genel talepler olup olmadığını kontrol et
  const isGenelTalepler = (request) => {
    const formData = request?.form_data?.form_data || request?.form_data || {};
    const category = formData.category || request?.category || '';
    return category === 'genel_talepler';
  };

  // Kategoriye göre onay modal başlığını getir
  const getApproveModalTitle = (request) => {
    const formData = request?.form_data?.form_data || request?.form_data || {};
    const category = formData.category || request?.category || '';
    
    const categoryTitles = {
      'ik': "İK Birimine İlet",
      'satin_alma': "Satın Alma Birimine İlet",
      'muhasebe': "Muhasebe Birimine İlet",
      'idari_isler': "İdari İşler Birimine İlet",
      'yonetim': "Yönetim Birimine İlet",
      'genel_talepler': 'Talebi Onayla'
    };
    
    return categoryTitles[category] || 'Talebi Onayla';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const RequestList = ({ requests, testId, emptyMessage, emptyIcon: EmptyIcon, showRejectionReason = false }) => (
    <Card className="border-[hsl(var(--border))]">
      <CardContent className="pt-6">
        {/* Seçim Toolbar */}
        {requests.length > 0 && (
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-[hsl(var(--border))]">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={requests.length > 0 && requests.every(r => selectedRequests.includes(r.id))}
                onCheckedChange={() => selectAllRequests(requests)}
                data-testid={`${testId}-select-all`}
              />
              <span className="text-sm text-[hsl(var(--muted-foreground))]">
                {selectedRequests.filter(id => requests.some(r => r.id === id)).length > 0 
                  ? `${selectedRequests.filter(id => requests.some(r => r.id === id)).length} seçili`
                  : 'Tümünü seç'}
              </span>
            </div>
            {selectedRequests.length > 0 && (
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearSelection}
                  className="text-gray-600"
                >
                  Seçimi Temizle
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => setShowDeleteConfirmModal(true)}
                  className="gap-1"
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="h-4 w-4" />
                  Sil ({selectedRequests.length})
                </Button>
              </div>
            )}
          </div>
        )}
        
        {requests.length === 0 ? (
          <div className="text-center py-16 text-[hsl(var(--muted-foreground))]" data-testid={`${testId}-empty`}>
            <EmptyIcon className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="font-medium" style={{ fontFamily: 'Karla, sans-serif' }}>{emptyMessage}</p>
          </div>
        ) : (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-4">
              {requests.map((request) => {
                const unread = isUnread(request);
                const isSelected = selectedRequests.includes(request.id);
                return (
                  <div 
                    key={request.id}
                    className={`p-4 border rounded-lg hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer ${
                      isSelected
                        ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/30 ring-2 ring-amber-200'
                        : unread 
                          ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/30 border-l-4 border-l-blue-500' 
                          : 'border-[hsl(var(--border))]'
                    }`}
                    onClick={() => handleViewRequest(request)}
                    data-testid={`${testId}-item-${request.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(e) => toggleRequestSelection(request.id, { stopPropagation: () => {} })}
                          data-testid={`checkbox-${request.id}`}
                        />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {unread && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" title="Okunmamış" />
                          )}
                          {request.form_number && (
                            <span className="text-xs font-mono text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))] px-2 py-0.5 rounded">
                              {request.form_number}
                            </span>
                          )}
                          <h3 className={`text-[hsl(var(--foreground))] ${unread ? 'font-bold' : 'font-semibold'}`} style={{ fontFamily: 'Chivo, sans-serif' }}>
                            {request.source === 'work_panel' ? request.title : request.form_template_name}
                          </h3>
                          {getStatusBadge(request.status)}
                        </div>
                        <p className={`text-sm text-[hsl(var(--muted-foreground))] ${unread ? 'font-medium' : ''}`} style={{ fontFamily: 'Karla, sans-serif' }}>
                          <strong>Talep Eden:</strong> {request.original_requester_name || request.requester_name || request.owner_name || request.form_data?.requester_name || request.user_name || 'Bilinmiyor'}
                        </p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                          Talep Tarihi: {formatDate(request.created_at)}
                        </p>
                        {/* Belge Adı */}
                        {(request.documents?.length > 0 || request.form_data?.belgeler?.length > 0) && (
                          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 flex items-center gap-1">
                            <FileText className="h-3 w-3 text-amber-500" />
                            <strong>Belge:</strong> {request.documents?.[0]?.name || request.form_data?.belgeler?.[0]?.name || 'Belge'}
                            {(request.documents?.length > 1 || request.form_data?.belgeler?.length > 1) && (
                              <span className="text-gray-400">+{(request.documents?.length || request.form_data?.belgeler?.length) - 1}</span>
                            )}
                          </p>
                        )}
                        {showRejectionReason && request.branch_rejection?.reason && (
                          <p className="text-sm text-red-600 mt-2 p-2 bg-red-50 rounded">
                            <strong>Red Nedeni:</strong> {request.branch_rejection.reason}
                          </p>
                        )}
                        {showRejectionReason && request.hr_rejection?.reason && (
                          <p className="text-sm text-red-600 mt-2 p-2 bg-red-50 rounded">
                            <strong>İK Red Nedeni:</strong> {request.hr_rejection.reason}
                          </p>
                        )}
                        {showRejectionReason && request.management_rejection?.reason && (
                          <p className="text-sm text-red-600 mt-2 p-2 bg-red-50 rounded">
                            <strong>Yönetim Red Nedeni:</strong> {request.management_rejection.reason}
                          </p>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" className="ml-2" onClick={(e) => { e.stopPropagation(); handleViewRequest(request); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="branch-communication">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Talepler
          </h1>
          {getTotalUnreadCount() > 0 && (
            <Badge variant="destructive" className="h-6 min-w-[24px] px-2 flex items-center justify-center text-sm font-bold animate-pulse">
              {getTotalUnreadCount()}
            </Badge>
          )}
        </div>
        <p className="text-[hsl(var(--muted-foreground))]" style={{ fontFamily: 'Karla, sans-serif' }}>
          Çalışan taleplerini yönetin
        </p>
      </div>

      {/* Ana Kategori Tabları */}
      <div className="flex flex-wrap gap-2 p-3 bg-[hsl(var(--muted))] rounded-lg">
        {mainCategories.map((cat) => {
          const allRequests = [...inboxRequests, ...forwardedRequests, ...approvedRequests, ...rejectedRequests, ...rejectedByHRRequests];
          const filteredCount = filterByCategory(allRequests, cat.id).length;
          const unreadCount = getCategoryUnreadCount(cat.id);
          return (
            <Button
              key={cat.id}
              variant={activeTab === cat.id ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab(cat.id)}
              className={`gap-1 ${activeTab === cat.id ? 'bg-[hsl(var(--primary))] text-white' : ''}`}
              data-testid={`main-tab-${cat.id}`}
            >
              {cat.label}
              {unreadCount > 0 ? (
                <Badge 
                  variant="destructive" 
                  className="ml-1 h-5 min-w-[20px] px-1 flex items-center justify-center text-xs animate-pulse"
                >
                  {unreadCount}
                </Badge>
              ) : filteredCount > 0 ? (
                <Badge 
                  variant={activeTab === cat.id ? "secondary" : "outline"} 
                  className="ml-1 h-5 min-w-[20px] px-1 flex items-center justify-center text-xs"
                >
                  {filteredCount}
                </Badge>
              ) : null}
            </Button>
          );
        })}
      </div>

      {/* Alt Tablar - Gelen, Giden, Onaylanan, Red Edilen + Kategoriye özel tablar */}
      <Tabs 
        value={activeTab === 'my_requests' ? myRequestsSubTab : subTab} 
        onValueChange={activeTab === 'my_requests' ? setMyRequestsSubTab : setSubTab} 
        className="space-y-4"
      >
        <TabsList className={`grid w-full max-w-4xl ${subTabs.length <= 4 ? 'grid-cols-4' : 'grid-cols-6'}`}>
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            const count = getSubTabCount(tab.id);
            const unreadCount = getSubTabUnreadCount(tab.id);
            return (
              <TabsTrigger 
                key={tab.id} 
                value={tab.id} 
                className="flex items-center gap-1 text-xs sm:text-sm px-2" 
                data-testid={`sub-tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                {unreadCount > 0 ? (
                  <Badge 
                    variant="destructive" 
                    className="ml-1 h-5 min-w-[20px] px-1 flex items-center justify-center text-xs animate-pulse"
                  >
                    {unreadCount}
                  </Badge>
                ) : count > 0 ? (
                  <Badge 
                    variant={tab.id === 'approved' || tab.id === 'dept_approved' ? 'outline' : 'secondary'} 
                    className={`ml-1 h-5 min-w-[20px] px-1 flex items-center justify-center text-xs ${
                      (tab.id === 'approved' || tab.id === 'dept_approved') ? 'text-green-600 border-green-600' : ''
                    }`}
                  >
                    {count}
                  </Badge>
                ) : null}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="incoming">
          <RequestList 
            requests={getFilteredRequests()} 
            testId="incoming-requests"
            emptyMessage="Gelen talep bulunmuyor"
            emptyIcon={Inbox}
          />
        </TabsContent>

        <TabsContent value="approved">
          <RequestList 
            requests={getFilteredRequests()} 
            testId="approved-requests"
            emptyMessage="Onaylanan talep bulunmuyor"
            emptyIcon={CheckCircle}
          />
        </TabsContent>

        <TabsContent value="rejected">
          <RequestList 
            requests={getFilteredRequests()} 
            testId="rejected-requests"
            emptyMessage="Red edilen talep bulunmuyor"
            emptyIcon={XCircle}
            showRejectionReason={true}
          />
        </TabsContent>

        {/* Kategoriye göre iletilen talepler */}
        {activeTab !== 'all' && activeTab !== 'yonetim' && (
          <TabsContent value="forwarded_dept">
            <RequestList 
              requests={getFilteredRequests()} 
              testId="forwarded-dept-requests"
              emptyMessage={`${getCategoryLabel(activeTab)}'ya iletilen talep bulunmuyor`}
              emptyIcon={Send}
            />
          </TabsContent>
        )}

        {/* Departman onaylanan talepler */}
        {activeTab !== 'all' && activeTab !== 'yonetim' && (
          <TabsContent value="dept_approved">
            <RequestList 
              requests={getFilteredRequests()} 
              testId="dept-approved-requests"
              emptyMessage={`${getCategoryLabel(activeTab)} onaylanan talep bulunmuyor`}
              emptyIcon={CheckCircle}
            />
          </TabsContent>
        )}

        {/* Departman reddedilen talepler */}
        {activeTab !== 'all' && activeTab !== 'yonetim' && activeTab !== 'my_requests' && (
          <TabsContent value="dept_rejected">
            <RequestList 
              requests={getFilteredRequests()} 
              testId="dept-rejected-requests"
              emptyMessage={`${getCategoryLabel(activeTab)} reddedilen talep bulunmuyor`}
              emptyIcon={XCircle}
              showRejectionReason={true}
            />
          </TabsContent>
        )}

        {/* TALEPLERİM Sekmeleri */}
        {activeTab === 'my_requests' && (
          <>
            <TabsContent value="pending">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">Bekleyen talepleriniz</p>
                  <Button 
                    onClick={openCreateRequestModal}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="create-request-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Yeni Talep Oluştur
                  </Button>
                </div>
                {myRequestsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                  </div>
                ) : getMyFilteredRequests().length > 0 ? (
                  <div className="space-y-3">
                    {getMyFilteredRequests().map((request) => (
                      <Card 
                        key={request.id} 
                        className="cursor-pointer hover:border-amber-300 transition-colors"
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowRequestDetailModal(true);
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-amber-50 text-amber-700">
                                  {getCategoryLabel(request.category)}
                                </Badge>
                                <span className="font-medium">{request.title}</span>
                              </div>
                              <p className="text-sm text-gray-500 line-clamp-2">{request.description}</p>
                              {(request.documents?.length > 0) && (
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <FileText className="h-3 w-3 text-amber-500" />
                                  <strong>Belge:</strong> {request.documents[0]?.name || 'Belge'}
                                  {request.documents.length > 1 && <span className="text-gray-400">+{request.documents.length - 1}</span>}
                                </p>
                              )}
                              <p className="text-xs text-gray-400">
                                {new Date(request.created_at).toLocaleDateString('tr-TR')} tarihinde oluşturuldu
                              </p>
                            </div>
                            <Badge className="bg-yellow-100 text-yellow-700">Bekliyor</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Clock className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Bekleyen talebiniz bulunmuyor</p>
                    <Button 
                      onClick={openCreateRequestModal}
                      className="mt-4 bg-amber-600 hover:bg-amber-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      İlk Talebinizi Oluşturun
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="approved">
              <div className="space-y-4">
                <p className="text-sm text-gray-500">Kabul edilen talepleriniz</p>
                {myRequestsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                  </div>
                ) : getMyFilteredRequests().length > 0 ? (
                  <div className="space-y-3">
                    {getMyFilteredRequests().map((request) => (
                      <Card 
                        key={request.id} 
                        className="cursor-pointer hover:border-green-300 transition-colors border-l-4 border-l-green-500"
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowRequestDetailModal(true);
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-green-50 text-green-700">
                                  {getCategoryLabel(request.category)}
                                </Badge>
                                <span className="font-medium">{request.title}</span>
                              </div>
                              <p className="text-sm text-gray-500 line-clamp-2">{request.description}</p>
                              {(request.documents?.length > 0) && (
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <FileText className="h-3 w-3 text-amber-500" />
                                  <strong>Belge:</strong> {request.documents[0]?.name || 'Belge'}
                                  {request.documents.length > 1 && <span className="text-gray-400">+{request.documents.length - 1}</span>}
                                </p>
                              )}
                              <p className="text-xs text-gray-400">
                                {new Date(request.created_at).toLocaleDateString('tr-TR')} tarihinde oluşturuldu
                              </p>
                            </div>
                            <Badge className="bg-green-100 text-green-700">Kabul Edildi</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Kabul edilen talebiniz bulunmuyor</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="rejected">
              <div className="space-y-4">
                <p className="text-sm text-gray-500">Red edilen talepleriniz</p>
                {myRequestsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                  </div>
                ) : getMyFilteredRequests().length > 0 ? (
                  <div className="space-y-3">
                    {getMyFilteredRequests().map((request) => (
                      <Card 
                        key={request.id} 
                        className="cursor-pointer hover:border-red-300 transition-colors border-l-4 border-l-red-500"
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowRequestDetailModal(true);
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-red-50 text-red-700">
                                  {getCategoryLabel(request.category)}
                                </Badge>
                                <span className="font-medium">{request.title}</span>
                              </div>
                              <p className="text-sm text-gray-500 line-clamp-2">{request.description}</p>
                              {(request.documents?.length > 0) && (
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <FileText className="h-3 w-3 text-amber-500" />
                                  <strong>Belge:</strong> {request.documents[0]?.name || 'Belge'}
                                  {request.documents.length > 1 && <span className="text-gray-400">+{request.documents.length - 1}</span>}
                                </p>
                              )}
                              {request.rejection_reason && (
                                <p className="text-sm text-red-600 mt-2">
                                  <strong>Red Sebebi:</strong> {request.rejection_reason}
                                </p>
                              )}
                              <p className="text-xs text-gray-400">
                                {new Date(request.created_at).toLocaleDateString('tr-TR')} tarihinde oluşturuldu
                              </p>
                            </div>
                            <Badge className="bg-red-100 text-red-700">Red Edildi</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <XCircle className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Red edilen talebiniz bulunmuyor</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </>
        )}
      </Tabs>

      {/* Request Detail Modal */}
      <Dialog open={showRequestDetailModal} onOpenChange={setShowRequestDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <FileText className="h-5 w-5" />
              {selectedRequest?.source === 'work_panel' ? selectedRequest?.title : selectedRequest?.form_template_name}
              {selectedRequest?.form_number && (
                <span className="text-sm font-mono text-[hsl(var(--muted-foreground))] ml-2">
                  {selectedRequest.form_number}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Talep detaylarını görüntüleyin ve işlem yapın
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[hsl(var(--muted-foreground))]">Talep Eden</Label>
                  <p className="font-medium">
                    {selectedRequest.original_requester_name || 
                     selectedRequest.requester_name || 
                     selectedRequest.owner_name || 
                     selectedRequest.form_data?.requester_name ||
                     selectedRequest.user_name ||
                     'Bilinmiyor'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-[hsl(var(--muted-foreground))]">Talep Tarihi</Label>
                  <p className="font-medium">{formatDate(selectedRequest.created_at)}</p>
                </div>
                {(selectedRequest.form_number || selectedRequest.form_data?.form_number) && (
                  <div>
                    <Label className="text-xs text-[hsl(var(--muted-foreground))]">Form No</Label>
                    <p className="font-mono">{selectedRequest.form_number || selectedRequest.form_data?.form_number}</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-[hsl(var(--muted-foreground))]">Durum</Label>
                  <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                </div>
              </div>
              
              {/* Work Request Title & Description (İş Paneli talepleri için veya title/description varsa) */}
              {(selectedRequest.source === 'work_panel' || selectedRequest.title || selectedRequest.description) && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Talep Detayları
                  </Label>
                  <div className="mt-2 space-y-3 bg-[hsl(var(--muted))] p-4 rounded-lg">
                    {selectedRequest.title && (
                      <div className="flex justify-between items-center text-sm border-b border-[hsl(var(--border))] pb-2">
                        <span className="text-[hsl(var(--muted-foreground))] font-medium">Talep Başlığı:</span>
                        <span className="font-semibold text-[hsl(var(--foreground))]">{selectedRequest.title}</span>
                      </div>
                    )}
                    {selectedRequest.description && (
                      <div className="text-sm">
                        <span className="text-[hsl(var(--muted-foreground))] font-medium block mb-1">Açıklama:</span>
                        <p className="text-[hsl(var(--foreground))] bg-white p-3 rounded border">{selectedRequest.description}</p>
                      </div>
                    )}
                    {selectedRequest.priority && (
                      <div className="flex justify-between items-center text-sm border-t border-[hsl(var(--border))] pt-2">
                        <span className="text-[hsl(var(--muted-foreground))] font-medium">Öncelik:</span>
                        <span className="font-semibold text-[hsl(var(--foreground))]">
                          {selectedRequest.priority === 'low' ? 'Düşük' : 
                           selectedRequest.priority === 'normal' ? 'Normal' : 
                           selectedRequest.priority === 'high' ? 'Yüksek' : 
                           selectedRequest.priority === 'urgent' ? 'Acil' : selectedRequest.priority}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Form Data - Formatted Display (HR Request formları için) */}
              {selectedRequest.form_data && (
              <div className="border-t pt-4">
                <Label className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Form Detayları
                </Label>
                <div className="mt-2 space-y-3 bg-[hsl(var(--muted))] p-4 rounded-lg">
                  {(() => {
                    // Handle nested form_data structure
                    const formData = selectedRequest.form_data.form_data || selectedRequest.form_data;
                    
                    // Fields to hide from display (technical fields)
                    const hiddenFields = ['uploaded_file', 'form_type', 'form_data', 'mesaiFormDosya', 'formOlusturmaTarihi'];
                    
                    // Label mappings for better display
                    const labelMap = {
                      mesaiTarihi: 'Mesai Tarihi',
                      mesaiSaati: 'Mesai Saati',
                      mesaiGunu: 'Mesai Türü',
                      mesaiNedeni: 'Mesai Nedeni',
                      amount: 'Miktar',
                      installments: 'Taksit Sayısı',
                      reason: 'Talep Nedeni',
                      startDate: 'Başlangıç Tarihi',
                      endDate: 'Bitiş Tarihi',
                      izinTuru: 'İzin Türü',
                      // İş Paneli alanları
                      category: 'Kategori',
                      title: 'Talep Başlığı',
                      description: 'Açıklama',
                      priority: 'Öncelik'
                    };
                    
                    // Value mappings
                    const valueMap = {
                      is_gunu: 'Gün İçi Mesai (Hafta İçi)',
                      tatil: 'Tatil Mesaisi (Hafta Sonu/Resmi Tatil)',
                      overtime: 'Fazla Mesai',
                      advance: 'Avans',
                      leave: 'İzin',
                      // Kategori değerleri
                      ik: 'İK',
                      satin_alma: 'Satın Alma',
                      muhasebe: 'Muhasebe',
                      idari_isler: 'İdari İşler',
                      genel_talepler: 'Genel Talepler',
                      // Öncelik değerleri
                      low: 'Düşük',
                      normal: 'Normal',
                      high: 'Yüksek',
                      urgent: 'Acil'
                    };
                    
                    return Object.entries(formData)
                      .filter(([key, value]) => !hiddenFields.includes(key) && value !== null && value !== undefined && value !== '')
                      .map(([key, value]) => {
                        const label = labelMap[key] || key;
                        let displayValue = valueMap[value] || value;
                        
                        // Format date values
                        if (key.toLowerCase().includes('tarih') || key.toLowerCase().includes('date') || key.toLowerCase().includes('baslangic') || key.toLowerCase().includes('bitis')) {
                          try {
                            const date = new Date(value);
                            if (!isNaN(date.getTime())) {
                              displayValue = date.toLocaleDateString('tr-TR');
                            }
                          } catch {
                            // Keep original value
                          }
                        }
                        
                        // Format number values
                        if (key === 'mesaiSaati') {
                          displayValue = `${value} saat`;
                        }
                        if (key === 'amount') {
                          displayValue = `${Number(value).toLocaleString('tr-TR')} ₺`;
                        }
                        
                        return (
                          <div key={key} className="flex justify-between items-center text-sm border-b border-[hsl(var(--border))] pb-2 last:border-0 last:pb-0">
                            <span className="text-[hsl(var(--muted-foreground))] font-medium">{label}:</span>
                            <span className="font-semibold text-[hsl(var(--foreground))]">{String(displayValue)}</span>
                          </div>
                        );
                      });
                  })()}
                </div>
              </div>
              )}

              {/* Uploaded Document */}
              {selectedRequest.form_data && (() => {
                const formData = selectedRequest.form_data.form_data || selectedRequest.form_data;
                const fileData = formData.mesaiFormDosya || formData.uploaded_file || selectedRequest.form_data.uploaded_file;
                if (!fileData) return null;
                
                return (
                <div className="border-t pt-4">
                  <Label className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-2">
                    <FileCheck className="h-4 w-4" />
                    Yüklenen Belge
                  </Label>
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded">
                        <FileText className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Islak İmzalı Form</p>
                        <p className="text-xs text-gray-500">
                          {fileData?.includes('image/png') ? 'PNG Resim' : 
                           fileData?.includes('image/jpeg') || fileData?.includes('image/jpg') ? 'JPEG Resim' : 
                           fileData?.includes('application/pdf') ? 'PDF Dosyası' : 'Belge'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          if (fileData) {
                            const newWindow = window.open();
                            if (newWindow) {
                              if (fileData.startsWith('data:image/')) {
                                newWindow.document.write(`<html><head><title>Belge Önizleme</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f0f0f0;"><img src="${fileData}" style="max-width:100%;max-height:100vh;object-fit:contain;" /></body></html>`);
                              } else if (fileData.startsWith('data:application/pdf')) {
                                newWindow.document.write(`<html><head><title>PDF Önizleme</title></head><body style="margin:0;"><iframe src="${fileData}" style="width:100%;height:100vh;border:none;"></iframe></body></html>`);
                              } else {
                                newWindow.location.href = fileData;
                              }
                            }
                          }
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Görüntüle
                      </Button>
                      <Button 
                        size="sm" 
                        variant="default"
                        onClick={() => {
                          if (fileData) {
                            const link = document.createElement('a');
                            link.href = fileData;
                            let ext = 'pdf';
                            if (fileData.includes('image/png')) ext = 'png';
                            else if (fileData.includes('image/jpeg') || fileData.includes('image/jpg')) ext = 'jpg';
                            link.download = `form_${selectedRequest.form_number || 'belge'}.${ext}`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        }}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        İndir
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })()}

              {/* İzin Belgeleri */}
              {selectedRequest.form_data && (() => {
                const formData = selectedRequest.form_data.form_data || selectedRequest.form_data;
                const izinBelgeleri = formData.izinBelgeleri || [];
                if (izinBelgeleri.length === 0) return null;
                
                return (
                  <div className="border-t pt-4">
                    <Label className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-2">
                      <FileCheck className="h-4 w-4" />
                      İzin Belgeleri ({izinBelgeleri.length} dosya)
                    </Label>
                    <div className="mt-2 space-y-2">
                      {izinBelgeleri.map((belge, idx) => (
                        <div key={idx} className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-cyan-100 rounded">
                              <FileText className="h-5 w-5 text-cyan-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{belge.name}</p>
                              <p className="text-xs text-gray-500">
                                {belge.type || 'Belge'}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                const newWindow = window.open();
                                if (newWindow) {
                                  if (belge.data.startsWith('data:image/')) {
                                    newWindow.document.write(`<html><head><title>Belge Önizleme</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f0f0f0;"><img src="${belge.data}" style="max-width:100%;max-height:100vh;object-fit:contain;" /></body></html>`);
                                  } else if (belge.data.startsWith('data:application/pdf')) {
                                    newWindow.document.write(`<html><head><title>PDF Önizleme</title></head><body style="margin:0;"><iframe src="${belge.data}" style="width:100%;height:100vh;border:none;"></iframe></body></html>`);
                                  } else {
                                    newWindow.location.href = belge.data;
                                  }
                                }
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Görüntüle
                            </Button>
                            <Button 
                              size="sm" 
                              variant="default"
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = belge.data;
                                link.download = belge.name;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              İndir
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* İş Paneli Belgeleri */}
              {selectedRequest.documents && selectedRequest.documents.length > 0 && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-2">
                    <FileCheck className="h-4 w-4" />
                    Yüklenen Belgeler ({selectedRequest.documents.length} dosya)
                  </Label>
                  <div className="mt-2 space-y-2">
                    {selectedRequest.documents.map((doc, idx) => (
                      <div key={idx} className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded">
                            <FileText className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{doc.name}</p>
                            <p className="text-xs text-gray-500">
                              {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : 'Belge'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              const newWindow = window.open();
                              if (newWindow && doc.data) {
                                if (doc.data.startsWith('data:image/')) {
                                  newWindow.document.write(`<html><head><title>Belge Önizleme</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f0f0f0;"><img src="${doc.data}" style="max-width:100%;max-height:100vh;object-fit:contain;" /></body></html>`);
                                } else if (doc.data.startsWith('data:application/pdf')) {
                                  newWindow.document.write(`<html><head><title>PDF Önizleme</title></head><body style="margin:0;"><iframe src="${doc.data}" style="width:100%;height:100vh;border:none;"></iframe></body></html>`);
                                } else {
                                  newWindow.location.href = doc.data;
                                }
                              }
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Görüntüle
                          </Button>
                          <Button 
                            size="sm" 
                            variant="default"
                            onClick={() => {
                              if (doc.data) {
                                const link = document.createElement('a');
                                link.href = doc.data;
                                link.download = doc.name || 'belge';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            }}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            İndir
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedRequest.notes && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-[hsl(var(--muted-foreground))]">Notlar</Label>
                  <p className="mt-1 text-sm">{selectedRequest.notes}</p>
                </div>
              )}

              {/* Status History */}
              {selectedRequest.status_history && selectedRequest.status_history.length > 0 && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-[hsl(var(--muted-foreground))]">İşlem Geçmişi</Label>
                  <div className="mt-2 space-y-2">
                    {selectedRequest.status_history.map((history, idx) => (
                      <div key={idx} className="text-xs bg-[hsl(var(--muted))] p-2 rounded flex justify-between">
                        <span>{history.note}</span>
                        <span className="text-[hsl(var(--muted-foreground))]">{formatDate(history.timestamp)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action History (for work_requests) */}
              {selectedRequest.action_history && selectedRequest.action_history.length > 0 && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-2">
                    <History className="h-4 w-4" />
                    İşlem Geçmişi
                  </Label>
                  <div className="mt-2 space-y-3">
                    {selectedRequest.action_history.map((action, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className={`mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                          action.action === 'created' ? 'bg-blue-500' :
                          action.action === 'approved' ? 'bg-green-500' :
                          action.action === 'rejected' ? 'bg-red-500' :
                          action.action === 'forwarded' ? 'bg-amber-500' :
                          'bg-gray-400'
                        }`}></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-gray-900">
                              {action.action === 'created' ? 'Oluşturuldu' :
                               action.action === 'approved' ? 'Onaylandı' :
                               action.action === 'rejected' ? 'Reddedildi' :
                               action.action === 'forwarded' ? 'İletildi' :
                               action.action}
                            </span>
                            {action.target && (
                              <Badge variant="outline" className="text-xs">
                                <ArrowRight className="h-3 w-3 mr-1" />
                                {action.target === 'ik' ? 'İK' :
                                 action.target === 'muhasebe' ? 'Muhasebe' :
                                 action.target === 'satin_alma' ? 'Satın Alma' :
                                 action.target === 'idari_isler' ? 'İdari İşler' :
                                 action.target === 'yonetim' ? 'Yönetim' :
                                 action.target}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                            <User className="h-3 w-3" />
                            <span>{action.user_name || 'Bilinmiyor'}</span>
                            <span>•</span>
                            <span>{formatDate(action.timestamp)}</span>
                          </div>
                          {action.note && (
                            <p className="text-sm text-gray-600 mt-1 bg-gray-50 p-2 rounded">{action.note}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* HR Approval Info */}
              {selectedRequest.hr_approval && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-[hsl(var(--muted-foreground))]">İK İşlemi</Label>
                  <div className={`mt-2 p-3 rounded text-sm ${selectedRequest.hr_approval.approved ? 'bg-green-50' : 'bg-purple-50'}`}>
                    <p><strong>{selectedRequest.hr_approval.approved ? 'Onaylayan' : 'İleten'}:</strong> {selectedRequest.hr_approval.approved_by_name || selectedRequest.hr_approval.forwarded_by_name}</p>
                    {selectedRequest.hr_approval.note && (
                      <p><strong>Not:</strong> {selectedRequest.hr_approval.note}</p>
                    )}
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                      {formatDate(selectedRequest.hr_approval.timestamp)}
                    </p>
                  </div>
                </div>
              )}

              {/* HR Rejection Info */}
              {selectedRequest.hr_rejection && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-[hsl(var(--muted-foreground))]">İK Reddi</Label>
                  <div className="mt-2 bg-red-50 p-3 rounded text-sm">
                    <p><strong>Reddeden:</strong> {selectedRequest.hr_rejection.rejected_by_name}</p>
                    {selectedRequest.hr_rejection.reason && (
                      <p><strong>Red Nedeni:</strong> {selectedRequest.hr_rejection.reason}</p>
                    )}
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                      {formatDate(selectedRequest.hr_rejection.timestamp)}
                    </p>
                  </div>
                </div>
              )}

              {/* Management Approval Info */}
              {selectedRequest.management_approval && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-[hsl(var(--muted-foreground))]">Yönetim Onayı</Label>
                  <div className="mt-2 bg-green-50 p-3 rounded text-sm">
                    <p><strong>Onaylayan:</strong> {selectedRequest.management_approval.approved_by_name}</p>
                    {selectedRequest.management_approval.note && (
                      <p><strong>Not:</strong> {selectedRequest.management_approval.note}</p>
                    )}
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                      {formatDate(selectedRequest.management_approval.timestamp)}
                    </p>
                  </div>
                </div>
              )}

              {/* Management Rejection Info */}
              {selectedRequest.management_rejection && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-[hsl(var(--muted-foreground))]">Yönetim Reddi</Label>
                  <div className="mt-2 bg-red-50 p-3 rounded text-sm">
                    <p><strong>Reddeden:</strong> {selectedRequest.management_rejection.rejected_by_name}</p>
                    {selectedRequest.management_rejection.reason && (
                      <p><strong>Red Nedeni:</strong> {selectedRequest.management_rejection.reason}</p>
                    )}
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                      {formatDate(selectedRequest.management_rejection.timestamp)}
                    </p>
                  </div>
                </div>
              )}

              {/* Branch Rejection Info */}
              {selectedRequest.branch_rejection && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-[hsl(var(--muted-foreground))]">Şube Reddi</Label>
                  <div className="mt-2 bg-red-50 p-3 rounded text-sm">
                    <p><strong>Reddeden:</strong> {selectedRequest.branch_rejection.rejected_by_name}</p>
                    {selectedRequest.branch_rejection.reason && (
                      <p><strong>Red Nedeni:</strong> {selectedRequest.branch_rejection.reason}</p>
                    )}
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                      {formatDate(selectedRequest.branch_rejection.timestamp)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            {/* HR Form talepleri - İK'ya ilet */}
            {selectedRequest?.status === 'pending_branch' && !selectedRequest?.source && (
              <>
                <Button 
                  variant="destructive" 
                  onClick={() => openActionModal('reject')}
                  disabled={loading}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Red Et
                </Button>
                <Button 
                  onClick={() => openActionModal('forward_hr')}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  İK Birimine İlet
                </Button>
              </>
            )}
            {/* İş Paneli talepleri - Onayla/Reddet */}
            {selectedRequest?.source === 'work_panel' && selectedRequest?.status === 'pending' && (
              <>
                <Button 
                  variant="destructive" 
                  onClick={() => openActionModal('reject_work')}
                  disabled={loading}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Red Et
                </Button>
                <Button 
                  onClick={() => openActionModal('approve_only')}
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Onayla
                </Button>
                {isGenelTalepler(selectedRequest) ? (
                  <Button 
                    onClick={openUserSelectModal}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    {getApproveButtonText(selectedRequest)}
                  </Button>
                ) : (
                  <Button 
                    onClick={() => openActionModal('approve_work')}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {getApproveButtonText(selectedRequest)}
                  </Button>
                )}
              </>
            )}
            {/* İK Panel talepleri - Onayla/İlet/Reddet */}
            {selectedRequest?.source === 'hr_panel' && selectedRequest?.status === 'pending' && (
              <>
                <Button 
                  variant="destructive" 
                  onClick={() => openActionModal('reject_hr_panel')}
                  disabled={loading}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reddet
                </Button>
                <Button 
                  onClick={() => openActionModal('forward_hr_panel')}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Başkasına İlet
                </Button>
                <Button 
                  onClick={() => openActionModal('approve_hr_panel')}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Onayla
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowRequestDetailModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Modal (Forward/Reject with note) */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'forward_hr' ? 'İK Birimine İlet' : 
               actionType === 'approve_work' ? getApproveModalTitle(selectedRequest) :
               actionType === 'approve_only' ? 'Talebi Onayla' :
               actionType === 'reject_work' ? 'Talebi Reddet' : 
               actionType === 'approve_hr_panel' ? 'Talebi Onayla' :
               actionType === 'reject_hr_panel' ? 'Talebi Reddet' :
               actionType === 'forward_hr_panel' ? 'Başkasına İlet' : 'Talebi Reddet'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'forward_hr' 
                ? 'Talebi İK birimine iletmeden önce not ekleyebilirsiniz.'
                : actionType === 'approve_work'
                ? 'Talebi onaylayıp ilgili birime iletmeden önce not ekleyebilirsiniz.'
                : actionType === 'approve_only'
                ? 'Talebi onaylamadan önce not ekleyebilirsiniz. Onaylanan talep gönderenin "Onaylanan" kutusuna düşecektir.'
                : actionType === 'approve_hr_panel'
                ? 'Talebi onaylamak üzeresiniz.'
                : actionType === 'forward_hr_panel'
                ? 'Talebi başka birine iletmek için kişi seçin.'
                : 'Lütfen red nedenini belirtin.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* İletme için kişi seçimi */}
            {actionType === 'forward_hr_panel' && (
              <div>
                <Label>İletilecek Kişi *</Label>
                <Select value={forwardToUserId} onValueChange={setForwardToUserId}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Kişi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {forwardUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name} {user.position ? `(${user.position})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label htmlFor="actionNote">
                {(actionType === 'reject' || actionType === 'reject_work' || actionType === 'reject_hr_panel') ? 'Red Nedeni *' : 'Not (İsteğe bağlı)'}
              </Label>
              <Textarea
                id="actionNote"
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                placeholder={(actionType === 'reject' || actionType === 'reject_work' || actionType === 'reject_hr_panel') ? 'Red nedenini yazın...' : 'Not ekleyin...'}
                className="mt-2"
                rows={4}
              />
            </div>
            
            {/* Ek Belge Yükleme */}
            <div>
              <Label>Ek Belgeler (İsteğe bağlı)</Label>
              <div className="mt-2">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setActionDocuments(prev => [...prev, ...files]);
                  }}
                  className="hidden"
                  id="action-file-upload"
                />
                <label
                  htmlFor="action-file-upload"
                  className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <Upload className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Belge Yükle (PDF, JPG, PNG, DOC)</span>
                </label>
                {actionDocuments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {actionDocuments.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <span className="truncate flex-1">{file.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setActionDocuments(prev => prev.filter((_, i) => i !== idx));
                          }}
                        >
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleAction}
              disabled={loading || ((actionType === 'reject' || actionType === 'reject_work' || actionType === 'reject_hr_panel') && !actionNote.trim())}
              className={actionType === 'forward_hr' ? 'bg-blue-600 hover:bg-blue-700' : 
                        (actionType === 'approve_work' || actionType === 'approve_only') ? 'bg-green-600 hover:bg-green-700' : ''}
              variant={(actionType === 'reject' || actionType === 'reject_work' || actionType === 'reject_hr_panel') ? 'destructive' : 'default'}
            >
              {loading ? 'İşleniyor...' : 
               actionType === 'forward_hr' ? 'İlet' : 
               actionType === 'approve_work' ? getApproveButtonText(selectedRequest) : 
               actionType === 'approve_only' ? 'Onayla' : 'Reddet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Selection Modal (Genel Talepler için) */}
      <Dialog open={showUserSelectModal} onOpenChange={setShowUserSelectModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Kullanıcı Seçin
            </DialogTitle>
            <DialogDescription>
              Talebi iletmek istediğiniz kullanıcıyı seçin
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Arama */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="İsim, görev veya e-posta ile ara..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="user-search-input"
              />
            </div>
            
            {/* Kullanıcı Listesi */}
            <div className="border rounded-lg max-h-64 overflow-y-auto">
              {loadingUsers ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-2"></div>
                  Kullanıcılar yükleniyor...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  {userSearchTerm ? 'Sonuç bulunamadı' : 'Kullanıcı bulunamadı'}
                </div>
              ) : (
                <div className="divide-y">
                  {filteredUsers.map((user) => (
                    <div 
                      key={user.id}
                      onClick={() => setSelectedUserId(user.id)}
                      className={`p-3 cursor-pointer transition-colors hover:bg-gray-50 ${
                        selectedUserId === user.id 
                          ? 'bg-green-50 border-l-4 border-l-green-500' 
                          : ''
                      }`}
                      data-testid={`user-option-${user.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                            selectedUserId === user.id ? 'bg-green-500' : 'bg-gray-400'
                          }`}>
                            {user.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className={`font-medium ${selectedUserId === user.id ? 'text-green-700' : 'text-gray-900'}`}>
                              {user.name}
                            </p>
                            {user.position && (
                              <p className="text-sm text-gray-500">{user.position}</p>
                            )}
                          </div>
                        </div>
                        {selectedUserId === user.id && (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Not alanı */}
            <div>
              <Label htmlFor="forwardNote">Not (İsteğe bağlı)</Label>
              <Textarea
                id="forwardNote"
                value={forwardNote}
                onChange={(e) => setForwardNote(e.target.value)}
                placeholder="Kullanıcıya iletmek istediğiniz bir not ekleyin..."
                className="mt-2"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserSelectModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleForwardToUser}
              disabled={loading || !selectedUserId}
              className="bg-green-600 hover:bg-green-700"
              data-testid="forward-to-user-btn"
            >
              {loading ? 'İletiliyor...' : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Onayla ve İlet
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Silme Onay Modalı */}
      <Dialog open={showDeleteConfirmModal} onOpenChange={setShowDeleteConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Talepleri Sil
            </DialogTitle>
            <DialogDescription>
              {selectedRequests.length} adet talebi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteConfirmModal(false)}
              disabled={deleting}
            >
              İptal
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteSelected}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Siliniyor...' : `Evet, ${selectedRequests.length} Talebi Sil`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Yeni Talep Oluşturma Modalı */}
      <Dialog open={createRequestModalOpen} onOpenChange={setCreateRequestModalOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-amber-600" />
              Yeni Talep Oluştur
            </DialogTitle>
            <DialogDescription>
              Taleplerinizi ilgili departmana iletmek için formu doldurun.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Kategori Seçimi */}
            <div className="space-y-2">
              <Label>Kategori *</Label>
              <Select 
                value={newRequestData.category} 
                onValueChange={(value) => setNewRequestData(prev => ({...prev, category: value, title: ''}))}
              >
                <SelectTrigger data-testid="request-category-select">
                  <SelectValue placeholder="Kategori seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ik">İK</SelectItem>
                  <SelectItem value="satin_alma">Satın Alma</SelectItem>
                  <SelectItem value="muhasebe">Muhasebe</SelectItem>
                  <SelectItem value="idari_isler">İdari İşler</SelectItem>
                  <SelectItem value="genel_talepler">Genel Talepler</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* İş Konusu - Dosya adından otomatik */}
            <div className="space-y-2">
              <Label>İş Konusu <span className="text-xs text-gray-500">(Belge yüklendiğinde otomatik dolar)</span></Label>
              <Input 
                value={newRequestData.title} 
                readOnly
                placeholder="Belge yükleyin..."
                className="bg-gray-50"
                data-testid="request-title-input"
              />
            </div>

            {/* Belgeler - Tüm kategoriler için */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-amber-700">
                <Upload className="h-4 w-4" />
                Belge Yükle *
              </Label>
              <div className="border-2 border-dashed border-amber-300 rounded-lg p-4 bg-amber-50">
                <input
                  type="file"
                  multiple
                  onChange={handleDocumentSelect}
                  className="hidden"
                  id="request-document-upload"
                  data-testid="request-document-input"
                />
                <label 
                  htmlFor="request-document-upload" 
                  className="flex flex-col items-center cursor-pointer"
                >
                  <Upload className="h-8 w-8 text-amber-500 mb-2" />
                  <span className="text-sm text-amber-700 font-medium">Belge eklemek için tıklayın</span>
                </label>
              </div>
              
              {pendingDocuments.length > 0 && (
                <div className="space-y-2 mt-2">
                  {pendingDocuments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-amber-50 p-2 rounded-md border border-amber-200">
                      <div className="flex items-center gap-2">
                        <FileIcon className="h-4 w-4 text-amber-600" />
                        <span className="text-sm font-medium truncate max-w-[200px]">{file.name}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDocument(index)}
                        className="h-6 w-6 p-0 text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* İstek Zamanı - Tüm kategorilerde göster */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                İstek Zamanı
              </Label>
              <Input
                type="date"
                value={newRequestData.expected_delivery_date}
                onChange={(e) => setNewRequestData(prev => ({...prev, expected_delivery_date: e.target.value}))}
                className="w-full"
                data-testid="expected-delivery-date-input"
              />
              <p className="text-xs text-gray-500">Talebinizin ne zaman karşılanmasını istiyorsunuz?</p>
            </div>

            {/* Alıcı Seçimi - Muhasebe hariç */}
            {newRequestData.category !== 'muhasebe' && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Gönderilecek Kişi (Opsiyonel)
              </Label>
              <div className="border rounded-lg p-3 space-y-2">
                <Input
                  placeholder="Kullanıcı ara..."
                  value={requestUserSearchTerm}
                  onChange={(e) => setRequestUserSearchTerm(e.target.value)}
                  className="mb-2"
                  data-testid="request-user-search"
                />
                {loadingRequestUsers ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
                  </div>
                ) : (
                  <ScrollArea className="h-[150px]">
                    <div className="space-y-1">
                      {/* Seçim Yok Seçeneği */}
                      <div
                        className={`p-2 rounded-md cursor-pointer transition-colors ${
                          !newRequestData.target_user_id 
                            ? 'bg-amber-100 border border-amber-300' 
                            : 'hover:bg-gray-100'
                        }`}
                        onClick={() => setNewRequestData(prev => ({...prev, target_user_id: ''}))}
                      >
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">İlgili departmana gönder (Kişi seçme)</span>
                        </div>
                      </div>
                      {filteredRequestTargetUsers.map((user) => (
                        <div
                          key={user.id}
                          className={`p-2 rounded-md cursor-pointer transition-colors ${
                            newRequestData.target_user_id === user.id 
                              ? 'bg-amber-100 border border-amber-300' 
                              : 'hover:bg-gray-100'
                          }`}
                          onClick={() => setNewRequestData(prev => ({...prev, target_user_id: user.id}))}
                          data-testid={`user-option-${user.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{user.name}</p>
                              <p className="text-xs text-gray-500">{user.position || user.email}</p>
                            </div>
                            {newRequestData.target_user_id === user.id && (
                              <CheckCircle className="h-4 w-4 text-amber-600" />
                            )}
                          </div>
                        </div>
                      ))}
                      {filteredRequestTargetUsers.length === 0 && requestUserSearchTerm && (
                        <p className="text-sm text-gray-500 text-center py-4">Kullanıcı bulunamadı</p>
                      )}
                    </div>
                  </ScrollArea>
                )}
              </div>
              {newRequestData.target_user_id && (
                <p className="text-xs text-amber-600">
                  Seçilen: {requestTargetUsers.find(u => u.id === newRequestData.target_user_id)?.name}
                </p>
              )}
            </div>
            )}

            {/* Öncelik - Muhasebe hariç */}
            {newRequestData.category !== 'muhasebe' && (
            <div className="space-y-2">
              <Label>Öncelik</Label>
              <Select 
                value={newRequestData.priority} 
                onValueChange={(value) => setNewRequestData(prev => ({...prev, priority: value}))}
              >
                <SelectTrigger data-testid="request-priority-select">
                  <SelectValue placeholder="Öncelik seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Düşük</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Yüksek</SelectItem>
                  <SelectItem value="urgent">Acil</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}

            {/* Açıklama */}
            <div className="space-y-2">
              <Label>Açıklama {newRequestData.category !== 'muhasebe' ? '*' : '(Opsiyonel)'}</Label>
              <Textarea
                value={newRequestData.description}
                onChange={(e) => setNewRequestData(prev => ({...prev, description: e.target.value}))}
                placeholder={newRequestData.category === 'muhasebe' ? "Ek açıklama ekleyin (opsiyonel)..." : "Talebinizi detaylı açıklayın..."}
                rows={newRequestData.category === 'muhasebe' ? 2 : 4}
                data-testid="request-description-input"
              />
            </div>

            {/* Belgeler - Muhasebe hariç kategoriler için */}
            {newRequestData.category !== 'muhasebe' && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileIcon className="h-4 w-4" />
                Ekler (Opsiyonel)
              </Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  multiple
                  onChange={handleDocumentSelect}
                  className="hidden"
                  id="request-document-upload"
                  data-testid="request-document-input"
                />
                <label 
                  htmlFor="request-document-upload" 
                  className="flex flex-col items-center cursor-pointer"
                >
                  <Upload className="h-6 w-6 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Belge eklemek için tıklayın</span>
                </label>
              </div>
              
              {pendingDocuments.length > 0 && (
                <div className="space-y-2 mt-2">
                  {pendingDocuments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-md">
                      <div className="flex items-center gap-2">
                        <FileIcon className="h-4 w-4 text-amber-600" />
                        <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDocument(index)}
                        className="h-6 w-6 p-0 text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateRequestModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCreateRequest}
              disabled={submittingRequest || (newRequestData.category === 'muhasebe' && pendingDocuments.length === 0)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="submit-request-btn"
            >
              {submittingRequest ? 'Gönderiliyor...' : 'Talep Gönder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchCommunication;
